/**
 * What to say under a "Pending Signature" badge (RAC-10257).
 *
 * The signed counter is deliberately TENANTS-only — LeaseDocumentListBuilder
 * builds `tenant_count` and `signed_count` from the tenant signer rows and
 * leaves the landlord out of both, because putting their countersignature in
 * the numerator against a tenant denominator would read "2 of 1 signed".
 *
 * That is right on its own and wrong when read next to the badge. An uploaded
 * lease whose one tenant has signed shows "Pending Signature" above "1 of 1
 * signed", which looks like a contradiction: if everyone signed, why is it
 * pending? The missing party is the landlord, and the fraction has no room to
 * mention them.
 *
 * So once every tenant is done, stop counting and name what is actually being
 * waited on. The fraction has served its purpose by then — it only tells the
 * landlord something while tenants are still working through it.
 *
 * Scoped to uploaded leases. A non-lease document (RAC-10324) invites every
 * signer at once with no landlord hold-back, so there is no countersignature
 * to await and its counter covers all of its signers rather than a subset.
 */

/**
 * @param {boolean} notTracked   is this an uploaded lease? (the `not_tracked` flag)
 * @param {number}  tenantCount  tenants on the lease
 * @param {number}  signedCount  tenants who have signed
 * @returns {string|null}        the line to render, or null to render nothing
 */
export function signingProgressLabel({ notTracked, tenantCount, signedCount }) {
    const tenants = Number(tenantCount) || 0;
    const signed = Number(signedCount) || 0;

    if (tenants < 1) {
        return null;
    }

    // Only an uploaded lease holds the landlord back, and PrepareUploadedLeaseRequest
    // requires exactly one landlord alongside at least one tenant — so tenants
    // all signed means the landlord, and only the landlord, is outstanding.
    if (notTracked && signed >= tenants) {
        return 'Awaiting your countersignature';
    }

    return `${signed} of ${tenants} signed`;
}
